/**
 * The Editor canvas document: the email body the author is editing, placed
 * into the layout it will be sent in, for direct manipulation. It is a DISPLAY
 * copy and is never saved or sent.
 *
 * No Liquid is evaluated here. The body arrives already instrumented by the
 * builder's own projection (`waypointSource.canvas`), with each condition
 * showing the variation the author picked. Each plain `{{ path }}` output that
 * the canvas can show as text is displayed as the token catalogue's platform
 * sample (escaped text; the token stays in the tooltip). Anything else (filters,
 * tags, outputs inside attributes) stays literal. The Preview tab and the
 * preview pane show the server's render, which is authoritative.
 */
import { escText, markPlaceholders, stripEditorMeta } from "@v0y4ge/jrni-email-builder";
import { isolateEmailCanvas } from "../html-source-view";
import * as sections from "./layout/layout-sections";

const SLOT_SOURCE = "\\{\\{-?\\s*body\\s*-?\\}\\}";

/** Marked spans whose whole content is one plain token output. */
const MARKED = /(<span data-eb-ph="\d+" title="[^"]*">)\{\{-?\s*([\w.]+)\s*-?\}\}(<\/span>)/g;

export function displaySamples(html: string, samples: Readonly<Record<string, string>>): string {
  return html.replace(MARKED, (whole, open: string, path: string, close: string) => {
    const v = samples[path];
    return v == null ? whole : open + escText(v) + close;
  });
}

export type CanvasResult =
  | { ok: true; srcdoc: string; layoutShown: boolean }
  | { ok: false; reason: string; sectionError: boolean };

export interface ComposeInput {
  /** Published source of the layout the email is sent in, or null when none can be used. */
  layoutHtml: string | null;
  show: { header: boolean; footer: boolean };
  /** Instrumented body from `waypointSource.canvas(projection, choose)`. */
  bodyCanvas: string;
  samples: Readonly<Record<string, string>>;
}

export function composeCanvas(input: ComposeInput): CanvasResult {
  const body = displaySamples(markPlaceholders(input.bodyCanvas, { start: 0 }), input.samples);
  if (!input.layoutHtml) {
    return { ok: true, layoutShown: false, srcdoc: isolateEmailCanvas(`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:24px;background:#F4F5F7">${body}</body></html>`) };
  }
  let layout = stripEditorMeta(input.layoutHtml);
  if (!input.show.header || !input.show.footer) {
    const r = sections.apply(layout, { header: input.show.header, footer: input.show.footer });
    if (!r.ok) return { ok: false, sectionError: true, reason: r.reason || "" };
    layout = r.html;
  }
  const marked = displaySamples(markPlaceholders(layout, { skip: new RegExp(SLOT_SOURCE, "g"), start: 100000 }), input.samples);
  const slots = marked.match(new RegExp(SLOT_SOURCE, "g")) || [];
  if (slots.length !== 1) return { ok: false, sectionError: false, reason: slots.length ? `The layout has ${slots.length} body slots.` : "The layout has no body slot." };
  const composed = marked.replace(new RegExp(SLOT_SOURCE), () => body);
  return { ok: true, layoutShown: true, srcdoc: isolateEmailCanvas(composed) };
}

/** A server-rendered email (or layout) for display: the same isolation as the canvas. */
export function isolateRendered(html: string): string { return isolateEmailCanvas(html); }
